import { useCallback, useState } from 'react'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { CustomReportArgument, PrintStyleSettings } from '../../../../redux/context/customReports/types'
import { deliveryClient } from '../../../../services/api/deliveryClient'
import { buildReportFileName } from '../../../../utils/buildReportFileName'
import { autoDownload } from '../../../../utils/helpers'
import customReportEndpoints from '../../../../features/customReport/api/customReportEndpoints'

interface UseCustomReportExportOptions {
  reportId: number | undefined
  companyId: string
  companyName?: string
  contractId?: number | null
  date?: string | null
  language?: string
  args?: CustomReportArgument[] | null
}

export interface ExportDownloadOptions {
  printSettings: Partial<PrintStyleSettings>
  reportName?: string
  forcedBreaks?: Set<number>
}

const useCustomReportExport = (options: UseCustomReportExportOptions) => {
  const [isLoading, setIsLoading] = useState(false)
  const { notification } = App.useApp()
  const { t } = useTranslation()

  const download = useCallback(
    async ({ printSettings, reportName, forcedBreaks }: ExportDownloadOptions) => {
      if (!options.contractId || !options.reportId) return
      setIsLoading(true)
      try {
        const mergedSettings =
          forcedBreaks != null ? { ...printSettings, forcedBreaks: [...forcedBreaks] } : printSettings
        const response = await deliveryClient.post(
          customReportEndpoints.printExport(options.contractId),
          {
            reportId: options.reportId,
            companyId: options.companyId,
            date: options.date ? String(options.date) : undefined,
            language: options.language,
            args: options.args,
            printSettings: mergedSettings
          },
          { responseType: 'arraybuffer' }
        )
        const disposition = (response.headers['content-disposition'] as string) || ''
        const match = disposition.match(/filename="([^"]+)"/)
        const fileName =
          match?.[1] ??
          buildReportFileName(options.companyName, reportName, options.date, printSettings.outputFormat ?? 'pdf')
        autoDownload(fileName, response.data)
      } catch (error) {
        console.error('Export failed:', error)
        notification.error({
          message: t('customReportPrint:exportError'),
          description: error instanceof Error ? error.message : undefined
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      options.contractId,
      options.reportId,
      options.companyId,
      options.companyName,
      options.date,
      options.language,
      options.args,
      t,
      notification
    ]
  )

  return { download, isLoading }
}

export default useCustomReportExport
